import React, { useEffect, useMemo, useState } from "react";
import { api, formatError, loadError } from "@/lib/api";
import { Loading, LoadFailed } from "@/components/LoadState";
import { Crosshair } from "@/components/Crosshair";
import { toast } from "@/lib/toast";
import { SlidersHorizontal, Save, X, Lock, ShieldAlert, DollarSign, Percent } from "lucide-react";

const UNLIMITED = -1;

// Numeric fields rendered per tier column.
const NUM_FIELDS = [
  { key: "monthly_price_cents", label: "Monthly price", kind: "money" },
  { key: "annual_price_cents", label: "Annual price", kind: "money" },
  { key: "commission_pct", label: "Commission", kind: "pct" },
  { key: "free_listings", label: "Free listings / mo", kind: "int" },
  { key: "monthly_bumps_included", label: "Priority bumps / mo", kind: "int" },
  { key: "saved_searches_cap", label: "Saved searches", kind: "cap" },
  { key: "pricing_profiles_cap", label: "Pricing profiles", kind: "cap" },
  { key: "photos_per_listing", label: "Photos / listing", kind: "int" },
];

const IN = "w-full bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-sm text-right transition-colors";

function displayNum(kind, v) {
  if (v === null || v === undefined || v === "") return "";
  if (kind === "money") return (Number(v) / 100).toString();
  return Number(v).toString();
}

function parseNum(kind, raw) {
  const s = String(raw).trim();
  if (s === "") return null;
  const n = Number(s);
  if (Number.isNaN(n)) return null;
  if (kind === "money") return Math.round(n * 100);
  return n;
}

export default function AdminTierPermissions() {
  const [data, setData] = useState(null);
  const [nl, setNl] = useState({});
  const [feat, setFeat] = useState({});
  const [cat, setCat] = useState({});
  const [alc, setAlc] = useState({});
  const [pending, setPending] = useState([]); // {section,tier,key,value,confirm?,label}
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const { data } = await api.get("/admin/tier-permissions", { timeout: 10000 });
      setData(data);
      setNl(JSON.parse(JSON.stringify(data.numeric_limits || {})));
      setFeat(JSON.parse(JSON.stringify(data.features || {})));
      setCat(JSON.parse(JSON.stringify(data.categories || {})));
      setAlc(JSON.parse(JSON.stringify(data.a_la_carte || {})));
      setPending([]);
    } catch (e) {
      setErr(loadError(e, "Couldn't load tier permissions."));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const meta = data?.tier_meta || {};
  const liveTiers = useMemo(() => (data?.active_individual_tiers || ["standard", "elite"]), [data]);
  const bizTiers = useMemo(() => (data?.business_tiers || []), [data]);

  const upsert = (entry) => {
    setPending((p) => {
      const rest = p.filter((c) => !(c.section === entry.section && c.tier === entry.tier && c.key === entry.key));
      return [...rest, entry];
    });
  };

  const setNumeric = (tier, key, kind, raw) => {
    const value = parseNum(kind, raw);
    setNl((p) => ({ ...p, [tier]: { ...(p[tier] || {}), [key]: value } }));
    upsert({ section: "numeric", tier, key, value, label: `${meta[tier]?.label} · ${key}` });
  };

  const toggleFeature = (feature, tier, val) => {
    setFeat((p) => ({ ...p, [feature]: { ...(p[feature] || {}), [tier]: val } }));
    upsert({ section: "feature", tier, key: feature, value: val, label: `${feature} · ${meta[tier]?.label}` });
  };

  const toggleCat = (category, tier, val) => {
    setCat((p) => ({ ...p, [category]: { ...(p[category] || {}), tiers: { ...((p[category] || {}).tiers || {}), [tier]: val } } }));
    upsert({ section: "category", tier, key: category, value: val, label: `${category} · ${meta[tier]?.label}` });
  };

  const setCatState = (category, state) => {
    setCat((p) => ({ ...p, [category]: { ...(p[category] || {}), state } }));
    const entry = { section: "category_state", tier: null, key: category, value: state, label: `${category} state → ${state}` };
    if (state === "prohibited") entry.confirm = `PROHIBIT ${category}`;
    upsert(entry);
  };

  const setAlcVal = (key, raw) => {
    const n = Number(raw);
    const value = Number.isNaN(n) ? 0 : n;
    setAlc((p) => ({ ...p, [key]: value }));
    upsert({ section: "a_la_carte", tier: null, key, value, label: `A-la-carte · ${key}` });
  };

  const doSave = async () => {
    if (reason.trim().length < 10) { toast.error("A reason of at least 10 characters is required."); return; }
    setBusy(true);
    let ok = 0;
    try {
      for (const c of pending) {
        // eslint-disable-next-line no-await-in-loop
        await api.patch("/admin/tier-permissions", {
          section: c.section, tier: c.tier || undefined, key: c.key, value: c.value,
          reason: reason.trim(), confirm: c.confirm,
        });
        ok += 1;
      }
      toast.success(`Saved ${ok} change${ok === 1 ? "" : "s"}. The panel is the source of truth — projected everywhere.`);
      setReasonOpen(false); setReason("");
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(formatError(typeof d === "object" ? d.message : d) || "Save failed.");
    } finally { setBusy(false); }
  };

  if (err) return (
    <div className="text-foreground" data-testid="admin-tier-permissions">
      <LoadFailed reason={err} onRetry={load} testid="tp-load-failed" />
    </div>
  );
  if (!data) return <Loading label="Loading tier permissions…" testid="tp-loading" />;

  const TierNumericCard = ({ tier, available }) => (
    <div className={`border ${available ? "border-olive/50" : "border-sniper"} bg-surface p-4 flex flex-col`} data-testid={`tp-tier-card-${tier}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg uppercase tracking-tight text-foreground">{meta[tier]?.label || tier}</h3>
        {!available && (
          <span className="font-mono text-[9px] uppercase tracking-wide text-blaze border border-blaze/50 bg-blaze/10 px-1.5 py-0.5" data-testid={`tp-not-available-${tier}`}>
            Not yet available
          </span>
        )}
      </div>
      <div className="mt-3 space-y-2.5">
        {NUM_FIELDS.map((f) => {
          const raw = (nl[tier] || {})[f.key];
          const isUnlim = f.kind === "cap" && raw === UNLIMITED;
          return (
            <div key={f.key}>
              <label className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground flex items-center justify-between">
                <span>{f.label}</span>
                {f.kind === "cap" && (
                  <button
                    type="button"
                    onClick={() => setNumeric(tier, f.key, "int", isUnlim ? "0" : String(UNLIMITED))}
                    data-testid={`tp-unlim-${tier}-${f.key}`}
                    className={`text-[9px] px-1.5 py-0.5 border ${isUnlim ? "border-olive text-olive-bright bg-olive/10" : "border-sniper text-muted-foreground"}`}
                  >
                    {isUnlim ? "Unlimited ✓" : "Set unlimited"}
                  </button>
                )}
              </label>
              <div className="relative mt-1">
                {f.kind === "money" && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-xs">$</span>}
                <input
                  type="number"
                  step={f.kind === "pct" ? "0.05" : f.kind === "money" ? "0.01" : "1"}
                  disabled={isUnlim}
                  data-testid={`tp-num-${tier}-${f.key}`}
                  value={isUnlim ? "" : displayNum(f.kind, raw)}
                  placeholder={isUnlim ? "unlimited" : ""}
                  onChange={(e) => setNumeric(tier, f.key, f.kind, e.target.value)}
                  className={`${IN} ${f.kind === "money" ? "pl-6" : ""} ${f.kind === "pct" ? "pr-7" : ""} ${isUnlim ? "opacity-50" : ""}`}
                />
                {f.kind === "pct" && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-xs">%</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="text-foreground" data-testid="admin-tier-permissions">
      <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2">
        <SlidersHorizontal className="w-7 h-7 text-olive-bright" /> Tier Permissions
      </h2>
      <p className="text-sm font-body text-secondary-foreground mt-2 max-w-[70ch]">
        The single source of truth for every tier entitlement. Numbers set here are projected across the whole
        platform — commission, listing quotas, saved searches and pricing pages all read from this panel. No commission
        may be set below <strong>4.0%</strong>. Business tiers exist but are not yet available for assignment.
      </p>
      <Crosshair className="my-5" />

      {/* SECTION 3 — NUMERIC LIMITS (individual, live) */}
      <h3 className="font-heading text-xl uppercase tracking-tight text-foreground">Individual tiers</h3>
      <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-4 mt-3 max-w-[720px]" data-testid="tp-individual-grid">
        {liveTiers.map((t) => <TierNumericCard key={t} tier={t} available />)}
      </div>

      {/* Business placeholders */}
      <div className="mt-8 flex items-center gap-2" data-testid="tp-business-header">
        <ShieldAlert className="w-5 h-5 text-blaze" />
        <h3 className="font-heading text-xl uppercase tracking-tight text-foreground">Business tiers</h3>
        <span className="font-mono text-[10px] uppercase tracking-wide text-blaze">Not yet available · seeded for future pricing</span>
      </div>
      <div className="grid md:grid-cols-3 gap-4 mt-3" data-testid="tp-business-grid">
        {bizTiers.map((t) => <TierNumericCard key={t} tier={t} available={false} />)}
      </div>

      {/* A-LA-CARTE */}
      <h3 className="font-heading text-xl uppercase tracking-tight text-foreground mt-8">A-la-carte fees</h3>
      <div className="grid sm:grid-cols-2 gap-4 mt-3 max-w-[480px]" data-testid="tp-alc">
        {[["additional_listing_fee", "Additional listing"], ["additional_bump_fee", "Additional bump"]].map(([k, label]) => (
          <div key={k} className="border border-sniper bg-surface p-4">
            <label className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5 text-olive-bright" /> {label}
            </label>
            <div className="relative mt-1">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-xs">$</span>
              <input type="number" step="0.5" min="0" data-testid={`tp-alc-${k}`} value={alc[k] ?? ""}
                onChange={(e) => setAlcVal(k, e.target.value)} className={`${IN} pl-6`} />
            </div>
          </div>
        ))}
      </div>

      {/* SECTION 2 — FEATURE ACCESS */}
      <h3 className="font-heading text-xl uppercase tracking-tight text-foreground mt-8">Feature access</h3>
      <div className="border border-sniper bg-surface mt-3 overflow-x-auto max-w-[640px]" data-testid="tp-feature-matrix">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-sniper">
              <th className="text-left font-mono text-[10px] uppercase tracking-wide text-muted-foreground px-3 py-2">Feature</th>
              {liveTiers.map((t) => (
                <th key={t} className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground px-3 py-2 text-center">{meta[t]?.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.feature_ids || []).map((f) => (
              <tr key={f} className="border-b border-sniper/50">
                <td className="px-3 py-2 font-body text-secondary-foreground">{f.replace(/_/g, " ")}</td>
                {liveTiers.map((t) => (
                  <td key={t} className="px-3 py-2 text-center">
                    <input type="checkbox" data-testid={`tp-feature-${f}-${t}`} checked={!!(feat[f] || {})[t]}
                      onChange={(e) => toggleFeature(f, t, e.target.checked)} className="w-4 h-4 accent-[#57682A]" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* SECTION 1 — CATEGORY ACCESS */}
      <h3 className="font-heading text-xl uppercase tracking-tight text-foreground mt-8">Category access</h3>
      <div className="border border-sniper bg-surface mt-3 overflow-x-auto max-w-[720px]" data-testid="tp-category-matrix">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-sniper">
              <th className="text-left font-mono text-[10px] uppercase tracking-wide text-muted-foreground px-3 py-2">Category</th>
              <th className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground px-3 py-2">State</th>
              {liveTiers.map((t) => (
                <th key={t} className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground px-3 py-2 text-center">{meta[t]?.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(cat).map((c) => {
              const row = cat[c] || {};
              const prohibited = row.state === "prohibited";
              return (
                <tr key={c} className="border-b border-sniper/50">
                  <td className="px-3 py-2 font-body text-secondary-foreground">{c}</td>
                  <td className="px-3 py-2">
                    <select data-testid={`tp-cat-state-${c}`} value={row.state || "enabled"}
                      onChange={(e) => setCatState(c, e.target.value)}
                      className="bg-input border border-sniper px-2 py-1 font-mono text-xs uppercase text-foreground">
                      <option value="enabled">Enabled</option>
                      <option value="restricted">Restricted</option>
                      <option value="prohibited">Prohibited</option>
                    </select>
                  </td>
                  {liveTiers.map((t) => (
                    <td key={t} className="px-3 py-2 text-center">
                      {prohibited ? (
                        <Lock className="w-3.5 h-3.5 text-muted-foreground inline" />
                      ) : (
                        <input type="checkbox" data-testid={`tp-cat-${c}-${t}`} checked={!!(row.tiers || {})[t]}
                          onChange={(e) => toggleCat(c, t, e.target.checked)} className="w-4 h-4 accent-[#57682A]" />
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="h-24" />

      {/* Sticky save bar */}
      {pending.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-surface border-t border-olive px-6 py-3 flex items-center justify-between" data-testid="tp-save-bar">
          <span className="font-mono text-xs uppercase text-secondary-foreground">
            <span data-testid="tp-save-count" className="text-olive-bright font-bold">{pending.length}</span> unsaved change{pending.length === 1 ? "" : "s"}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => { load(); }} data-testid="tp-discard-btn"
              className="font-mono uppercase text-xs border border-sniper hover:border-white text-secondary-foreground px-3 py-2">
              Discard
            </button>
            <button onClick={() => setReasonOpen(true)} data-testid="tp-save-btn"
              className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase px-5 py-2">
              <Save className="w-4 h-4" /> Save
            </button>
          </div>
        </div>
      )}

      {reasonOpen && (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4" data-testid="tp-reason-modal">
          <div className="bg-surface border border-sniper w-full max-w-md p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl uppercase text-foreground">Confirm changes</h3>
              <button onClick={() => setReasonOpen(false)} data-testid="tp-reason-cancel"><X className="w-5 h-5 text-muted-foreground" /></button>
            </div>
            <p className="text-xs font-body text-secondary-foreground mt-2">
              Saving {pending.length} change{pending.length === 1 ? "" : "s"}. Every change is audited. A reason of at least 10 characters is required.
            </p>
            <ul className="mt-3 max-h-32 overflow-y-auto text-[11px] font-mono text-muted-foreground space-y-1">
              {pending.map((c, i) => <li key={i}>• {c.label}: <span className="text-foreground">{String(c.value)}</span></li>)}
            </ul>
            <textarea data-testid="tp-reason-input" value={reason} onChange={(e) => setReason(e.target.value)}
              rows={3} placeholder="Why this change? (audited)"
              className="w-full mt-3 bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-body text-sm" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setReasonOpen(false)} data-testid="tp-reason-cancel-2"
                className="font-mono uppercase text-xs border border-sniper text-secondary-foreground px-4 py-2">Cancel</button>
              <button onClick={doSave} disabled={busy || reason.trim().length < 10} data-testid="tp-reason-confirm"
                className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase px-5 py-2">
                <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
